import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Send,
  CheckCircle
} from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Nachricht erfolgreich gesendet!");
      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Fehler beim Senden. Bitte versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Phone,
      label: "Telefon",
      value: "06028 4062310",
      href: "tel:060284062310"
    },
    {
      icon: Mail,
      label: "E-Mail",
      value: "hallo@kauzlogistik.de",
      href: "mailto:hallo@kauzlogistik.de"
    },
    {
      icon: MapPin,
      label: "Adresse",
      value: "Industriestr. 18, 63834 Sulzbach",
      href: "https://maps.google.com/?q=Industriestr.+18,+63834+Sulzbach"
    },
    {
      icon: Clock,
      label: "Geschäftszeiten",
      value: "Mo-Fr: 08:00 - 17:00 Uhr",
      href: null
    }
  ];

  return (
    <div data-testid="contact-page">
      {/* Page Header */}
      <section className="page-header py-20 md:py-28 text-white">
        <div className="container mx-auto px-4 relative z-10">
          <span className="inline-block bg-orange-600 text-white text-sm uppercase tracking-widest font-bold px-4 py-2 mb-6">
            Kontakt
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            Kontaktieren Sie uns
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl">
            Wir freuen uns auf Ihre Nachricht und melden uns schnellstmöglich bei Ihnen.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 className="text-2xl font-bold text-slate-900 mb-8">
                So erreichen Sie uns
              </h2>
              
              <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  const Content = (
                    <div className="flex items-start gap-4" data-testid={`contact-info-${index}`}>
                      <div className="w-12 h-12 bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Icon className="text-orange-600" size={24} aria-hidden="true" />
                      </div>
                      <div>
                        <div className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                          {item.label}
                        </div>
                        <div className="text-slate-900 font-medium">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  );
                  
                  if (item.href) {
                    return (
                      <a 
                        key={item.label} 
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        className="block hover:bg-slate-50 p-2 -m-2 transition-colors"
                      >
                        {Content}
                      </a>
                    );
                  }
                  return <div key={item.label}>{Content}</div>;
                })}
              </div>
              
              {/* Company Info */}
              <div className="mt-12 p-6 bg-slate-50 border-l-4 border-orange-600">
                <h3 className="font-bold text-lg text-slate-900 mb-4">Firmendaten</h3>
                <div className="text-slate-600 space-y-2">
                  <p><strong>Kauz Logistik e.K.</strong></p>
                  <p>Geschäftsführer: Waldemar Kauz</p>
                  <p>Industriestr. 18</p>
                  <p>63834 Sulzbach</p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 p-8 md:p-10">
                <h2 className="text-2xl font-bold text-slate-900 mb-2">
                  Schreiben Sie uns
                </h2>
                <p className="text-slate-600 mb-8">
                  Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen.
                </p>

                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="mx-auto text-green-500 mb-4" size={64} aria-hidden="true" />
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">
                      Vielen Dank!
                    </h3>
                    <p className="text-slate-600">
                      Ihre Nachricht wurde erfolgreich gesendet. Wir werden uns schnellstmöglich bei Ihnen melden.
                    </p>
                    <Button 
                      className="mt-6 bg-orange-600 hover:bg-orange-700 text-white"
                      onClick={() => setSubmitted(false)}
                    >
                      Weitere Nachricht senden
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="name" className="text-slate-700 font-medium">
                          Name *
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          type="text"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihr Name"
                          data-testid="contact-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email" className="text-slate-700 font-medium">
                          E-Mail *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="ihre@email.de"
                          data-testid="contact-email-input"
                        />
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="phone" className="text-slate-700 font-medium">
                          Telefon
                        </Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihre Telefonnummer"
                          data-testid="contact-phone-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="company" className="text-slate-700 font-medium">
                          Unternehmen
                        </Label>
                        <Input
                          id="company"
                          name="company"
                          type="text"
                          value={formData.company}
                          onChange={handleChange}
                          className="mt-2 border-slate-300 focus:border-orange-500 bg-white"
                          placeholder="Ihr Unternehmen"
                          data-testid="contact-company-input"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-slate-700 font-medium">
                        Nachricht *
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        className="mt-2 border-slate-300 focus:border-orange-500 bg-white min-h-[150px]"
                        placeholder="Wie können wir Ihnen helfen?"
                        data-testid="contact-message-input"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      disabled={loading}
                      className="bg-orange-600 hover:bg-orange-700 text-white uppercase tracking-wider font-bold px-8 py-6"
                      data-testid="contact-submit-button"
                    >
                      {loading ? (
                        "Wird gesendet..."
                      ) : (
                        <>
                          Nachricht senden
                          <Send className="ml-2" size={18} aria-hidden="true" />
                        </>
                      )}
                    </Button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="h-96 bg-slate-200 relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="mx-auto text-slate-400 mb-4" size={48} aria-hidden="true" />
            <p className="text-slate-600 font-medium">
              Industriestr. 18, 63834 Sulzbach
            </p>
            <a 
              href="https://maps.google.com/?q=Industriestr.+18,+63834+Sulzbach"
              target="_blank"
              rel="noopener noreferrer"
              className="text-orange-600 hover:text-orange-700 mt-2 inline-block"
            >
              In Google Maps öffnen →
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
